import {
  BadgeCheckIcon,
  CalendarCheckIcon,
  CompressIcon,
  ConvertIcon,
  EditIcon,
  EnvelopeIcon,
  MergeIcon,
  OrganizeIcon,
  SignIcon,
} from '@/features/tool/components/modals/paywall/icons';

export const PAYWALL_SUPPORT_EMAIL = 'websupport@pdfgenius.com';

export const PAYWALL_LINKS = {
  subscription: 'https://pdfgenius.com/terms',
  refund: 'https://pdfgenius.com/refundpolicy',
  terms: 'https://pdfgenius.com/terms',
  privacy: 'https://pdfgenius.com/privacy',
} as const;

export const PAYWALL_PLAN_FEATURES = [
  'Unlimited edits and downloads',
  'Convert to any format',
  'Easily add e-signatures to your documents',
  'Edit text and images in your PDF',
  'Add comments, shapes, and highlights',
  'Faster processing (up to 2x)',
] as const;

export const PAYWALL_TOOL_FEATURES = [
  {
    title: 'Edit',
    description: 'All editing & annotation tools',
    color: '#00D7E9',
    Icon: EditIcon,
  },
  {
    title: 'Convert',
    description: 'Convert to/from any format',
    color: '#FF6B6B',
    Icon: ConvertIcon,
  },
  {
    title: 'Compress',
    description: 'Compress without losing quality',
    color: '#FF4B5C',
    Icon: CompressIcon,
  },
  {
    title: 'Organize',
    description: 'Reorder, split & manage PDFs',
    color: '#A66BFF',
    Icon: OrganizeIcon,
  },
  {
    title: 'Merge',
    description: 'Merge multiple files into one',
    color: '#39B8FF',
    Icon: MergeIcon,
  },
  {
    title: 'Sign & Secure',
    description: 'E-sign documents',
    color: '#F251E5',
    Icon: SignIcon,
  },
] as const;

export const PAYWALL_TRUST_BADGES = [
  { label: 'Cancel Anytime', Icon: CalendarCheckIcon },
  { label: '24/7 Customer Support', Icon: EnvelopeIcon },
  { label: '50+ MLN Users', Icon: BadgeCheckIcon },
] as const;

export const PAYWALL_EXTENSION_BADGE_COLORS: Record<string, string> = {
  pdf: 'bg-[#F43F3F]',
  docx: 'bg-[#1E3A8A]',
  doc: 'bg-[#1E3A8A]',
  xlsx: 'bg-[#16A34A]',
  xls: 'bg-[#16A34A]',
  pptx: 'bg-[#EA580C]',
  ppt: 'bg-[#EA580C]',
  jpg: 'bg-[#8B5CF6]',
  jpeg: 'bg-[#8B5CF6]',
  png: 'bg-[#8B5CF6]',
};

export const PAYWALL_EXTENSION_THUMB_COLORS: Record<string, string> = {
  pdf: 'bg-[#F43F3F] border-[#C91E1E]',
  docx: 'bg-[#1E3A8A] border-[#16295F]',
  doc: 'bg-[#1E3A8A] border-[#16295F]',
  xlsx: 'bg-[#16A34A] border-[#0F7A37]',
  xls: 'bg-[#16A34A] border-[#0F7A37]',
  pptx: 'bg-[#EA580C] border-[#B44409]',
  ppt: 'bg-[#EA580C] border-[#B44409]',
  jpg: 'bg-[#8B5CF6] border-[#6D3FD1]',
  jpeg: 'bg-[#8B5CF6] border-[#6D3FD1]',
  png: 'bg-[#8B5CF6] border-[#6D3FD1]',
};

export const PAYWALL_TESTIMONIALS = [
  {
    name: 'Maya Chen',
    comment:
      'I only needed to change a few lines in a contract, and it took less than five minutes.',
    date: 'July 18, 2026',
  },
  { name: 'jordanwrites', comment: 'best of best 🤩', date: 'July 31, 2026' },
  {
    name: 'Lucas Ferreira',
    comment:
      'The layout stayed exactly the same after I changed the text. That was the part I was most worried about.',
    date: 'July 11, 2026',
  },
  {
    name: 'Nina Patel',
    comment:
      'Used it to update an old invoice. Everything was straightforward and the export looked clean.',
    date: 'July 30, 2026',
  },
  {
    name: 'Sam R.',
    comment:
      'Pretty impressed with how quickly it processed my file. No unnecessary steps.',
    date: 'July 25, 2026',
  },
  {
    name: 'claire.m',
    comment:
      'I had to fix a scanned document for work and the text recognition worked better than expected.',
    date: 'August 1, 2026',
  },
  {
    name: 'Omar Haddad',
    comment:
      'This saved me from recreating an entire PDF from scratch. Definitely keeping it bookmarked.',
    date: 'July 18, 2026',
  },
  {
    name: 'ellie_w',
    comment:
      'The interface feels simple, but it still has all the tools I actually need.',
    date: 'July 3, 2026',
  },
  {
    name: 'Thomas Nguyen',
    comment:
      'I edited a 20-page document without any formatting issues. Really smooth experience overall.',
    date: 'July 16, 2026',
  },
  {
    name: 'Priya Shah',
    comment:
      'Needed to add comments and highlight a few sections before sending the file. Worked perfectly.',
    date: 'July 27, 2026',
  },
  {
    name: 'Alex Morgan',
    comment:
      'Not usually a fan of online PDF tools, but this one was quick, clear, and didn’t feel complicated.',
    date: 'July 20, 2026',
  },
  {
    name: 'Renée Dubois',
    comment:
      'I used it for a last-minute application update and finished everything from my laptop in a few minutes.',
    date: 'July 13, 2026',
  },
] as const;

export const PAYWALL_TESTIMONIALS_PER_PAGE = 3;

export const PAYWALL_VARIANT_BY_DESIGN = {
  1: 'paywall1',
  2: 'paywall2',
  3: 'paywall3',
} as const;

export const PAYWALL_MODAL_WIDTH_CLASS =
  '!mx-[10px] !my-0 max-h-[95vh] w-[95vw] max-w-[1000px] xl:max-w-[1340px]';

export const PAYWALL_CONTENT_MAX_WIDTH_CLASS = 'max-w-[1340px]';
